import React, { useEffect, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from '@/components/ui/toast';
import { OdrLoadingSpinner } from '@/components/ui/spinner';
import { useUser } from '@/store/useUser';
import { hasAnyPolicy } from '@/utils/policies';

const LIST_QUARANTINE_SITES = gql`
  query QuarantineSitesList(
    $page: Int
    $pageSize: Int
    $searchText: String
    $searchField: String
    $sortField: String
    $sortDirection: String
  ) {
    quarantineSitesList(
      page: $page
      pageSize: $pageSize
      searchText: $searchText
      searchField: $searchField
      sortField: $sortField
      sortDirection: $sortDirection
    ) {
      totalCount
      items {
        id
        enter_date
        site
        quarantined_through
        extended_to_1
        extended_to_2
        extended_to_3
        extended_to_4
        extended_to_5
        updated
      }
    }
  }
`;

const CREATE_QUARANTINE_SITE = gql`
  mutation CreateQuarantineSite($input: QuarantineSiteInput!) {
    createQuarantineSite(input: $input) {
      id
    }
  }
`;

const UPDATE_QUARANTINE_SITE = gql`
  mutation UpdateQuarantineSite($id: Int!, $input: QuarantineSiteInput!) {
    updateQuarantineSite(id: $id, input: $input) {
      id
    }
  }
`;

const UPDATE_QUARANTINE_SITE_CELL = gql`
  mutation UpdateQuarantineSiteCell($id: Int!, $field: String!, $value: String) {
    updateQuarantineSiteCell(id: $id, field: $field, value: $value) {
      id
      updated
    }
  }
`;

const DELETE_QUARANTINE_SITE = gql`
  mutation DeleteQuarantineSite($id: Int!) {
    deleteQuarantineSite(id: $id) {
      message
      responseCode
    }
  }
`;

const PAGE_SIZE = 25;

const INLINE_FIELDS: Array<keyof QuarantineSiteItem> = [
  'site',
  'enter_date',
  'quarantined_through',
  'extended_to_1',
  'extended_to_2',
  'extended_to_3',
  'extended_to_4',
  'extended_to_5',
];

type QuarantineSiteItem = {
  id: number;
  enter_date?: string | null;
  site?: string | null;
  quarantined_through?: string | null;
  extended_to_1?: string | null;
  extended_to_2?: string | null;
  extended_to_3?: string | null;
  extended_to_4?: string | null;
  extended_to_5?: string | null;
  updated?: string | null;
};

type QuarantineSitesData = {
  quarantineSitesList: {
    totalCount: number;
    items: QuarantineSiteItem[];
  };
};

type QuarantineSiteFormState = {
  id: number | null;
  site: string;
  enter_date: string;
  quarantined_through: string;
  extended_to_1: string;
  extended_to_2: string;
  extended_to_3: string;
  extended_to_4: string;
  extended_to_5: string;
};

const emptyFormState: QuarantineSiteFormState = {
  id: null,
  site: '',
  enter_date: '',
  quarantined_through: '',
  extended_to_1: '',
  extended_to_2: '',
  extended_to_3: '',
  extended_to_4: '',
  extended_to_5: '',
};

const formFields: Array<{ key: keyof QuarantineSiteFormState; label: string; type?: 'date' }> = [
  { key: 'site', label: 'Housing Site' },
  { key: 'enter_date', label: 'Date Enter Quarantine', type: 'date' },
  { key: 'quarantined_through', label: 'Quarantined Through', type: 'date' },
  { key: 'extended_to_1', label: 'Extended To 1', type: 'date' },
  { key: 'extended_to_2', label: 'Extended To 2', type: 'date' },
  { key: 'extended_to_3', label: 'Extended To 3', type: 'date' },
  { key: 'extended_to_4', label: 'Extended To 4', type: 'date' },
  { key: 'extended_to_5', label: 'Extended To 5', type: 'date' },
];

const normalizeText = (value: string) => {
  const raw = value.trim();
  if (!raw) {
    return '';
  }

  return raw;
};

const normalizeFormState = (item?: QuarantineSiteItem | null): QuarantineSiteFormState => {
  if (!item) {
    return emptyFormState;
  }

  return {
    id: item.id,
    site: item.site || '',
    enter_date: item.enter_date || '',
    quarantined_through: item.quarantined_through || '',
    extended_to_1: item.extended_to_1 || '',
    extended_to_2: item.extended_to_2 || '',
    extended_to_3: item.extended_to_3 || '',
    extended_to_4: item.extended_to_4 || '',
    extended_to_5: item.extended_to_5 || '',
  };
};

const toTimestampLabel = (value: string | null | undefined) => {
  if (!value) {
    return '';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString();
};

export const QuarantineSites = () => {
  const { user, isUserLoading } = useUser();
  const canAccess = hasAnyPolicy(user, ['tools:quarantine~full']);

  const [searchText, setSearchText] = useState('');
  const [sortField, setSortField] = useState<keyof QuarantineSiteItem>('id');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formState, setFormState] = useState<QuarantineSiteFormState>(emptyFormState);
  const [cellDrafts, setCellDrafts] = useState<Record<string, string>>({});
  const [savingCellKey, setSavingCellKey] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { data, loading, error, refetch } = useQuery<QuarantineSitesData>(LIST_QUARANTINE_SITES, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: !canAccess,
    variables: {
      page,
      pageSize: PAGE_SIZE,
      searchText: searchText.trim() || null,
      searchField: 'all',
      sortField,
      sortDirection,
    },
  });

  const [createSite] = useMutation(CREATE_QUARANTINE_SITE);
  const [updateSite] = useMutation(UPDATE_QUARANTINE_SITE);
  const [updateSiteCell] = useMutation(UPDATE_QUARANTINE_SITE_CELL);
  const [deleteSite] = useMutation(DELETE_QUARANTINE_SITE);

  const items = data?.quarantineSitesList?.items || [];
  const totalCount = data?.quarantineSitesList?.totalCount || 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  useEffect(() => {
    if (page <= totalPages) {
      return;
    }

    setPage(totalPages);
  }, [page, totalPages]);

  const setField = (key: keyof QuarantineSiteFormState, value: string) => {
    setFormState((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const openCreateDialog = () => {
    setFormState(emptyFormState);
    setDialogOpen(true);
  };

  const openEditDialog = (item: QuarantineSiteItem) => {
    setFormState(normalizeFormState(item));
    setDialogOpen(true);
  };

  const getCellKey = (id: number, field: keyof QuarantineSiteItem) => `${id}:${field}`;

  const getCellValue = (item: QuarantineSiteItem, field: keyof QuarantineSiteItem) => {
    const key = getCellKey(item.id, field);
    if (Object.prototype.hasOwnProperty.call(cellDrafts, key)) {
      return cellDrafts[key];
    }

    return `${item[field] || ''}`;
  };

  const setCellValue = (item: QuarantineSiteItem, field: keyof QuarantineSiteItem, value: string) => {
    const key = getCellKey(item.id, field);
    setCellDrafts((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const commitCellValue = async (item: QuarantineSiteItem, field: keyof QuarantineSiteItem) => {
    const key = getCellKey(item.id, field);
    const draftValue = Object.prototype.hasOwnProperty.call(cellDrafts, key)
      ? cellDrafts[key]
      : `${item[field] || ''}`;
    const normalizedDraft = normalizeText(draftValue);
    const currentValue = normalizeText(`${item[field] || ''}`);

    if (normalizedDraft === currentValue) {
      return;
    }

    setSavingCellKey(key);

    try {
      await updateSiteCell({
        variables: {
          id: item.id,
          field,
          value: normalizedDraft,
        },
      });
      toast.success('Quarantine site updated');
      await refetch();
      setCellDrafts((current) => {
        const next = { ...current };
        delete next[key];
        return next;
      });
    } catch (mutationError: any) {
      toast.error(mutationError.message || 'Failed to update quarantine site');
    }

    setSavingCellKey(null);
  };

  const handleSave = async () => {
    setIsSaving(true);

    try {
      const input = {
        site: normalizeText(formState.site),
        enter_date: normalizeText(formState.enter_date),
        quarantined_through: normalizeText(formState.quarantined_through),
        extended_to_1: normalizeText(formState.extended_to_1),
        extended_to_2: normalizeText(formState.extended_to_2),
        extended_to_3: normalizeText(formState.extended_to_3),
        extended_to_4: normalizeText(formState.extended_to_4),
        extended_to_5: normalizeText(formState.extended_to_5),
      };

      if (formState.id === null) {
        await createSite({ variables: { input } });
        toast.success('Quarantine site created');
      }

      if (formState.id !== null) {
        await updateSite({
          variables: {
            id: formState.id,
            input,
          },
        });
        toast.success('Quarantine site updated');
      }

      setDialogOpen(false);
      await refetch();
    } catch (mutationError: any) {
      toast.error(mutationError.message || 'Failed to save quarantine site');
    }

    setIsSaving(false);
  };

  const handleDelete = async (item: QuarantineSiteItem) => {
    if (!window.confirm(`Delete quarantine site row ${item.id}?`)) {
      return;
    }

    setIsDeleting(true);

    try {
      await deleteSite({ variables: { id: item.id } });
      toast.success('Quarantine site deleted');
      await refetch();
    } catch (mutationError: any) {
      toast.error(mutationError.message || 'Failed to delete quarantine site');
    }

    setIsDeleting(false);
  };

  const handleRefresh = async () => {
    try {
      await refetch();
      toast.success('Quarantine Sites refreshed');
    } catch (refetchError: any) {
      toast.error(refetchError.message || 'Failed to refresh quarantine sites');
    }
  };

  const handleSort = (field: keyof QuarantineSiteItem) => {
    if (sortField === field) {
      setPage(1);
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setPage(1);
    setSortField(field);
    setSortDirection('asc');
  };

  if (isUserLoading) {
    return <OdrLoadingSpinner message="Loading Quarantine Sites" />;
  }

  if (!canAccess) {
    return (
      <div className="p-6">
        <h2 className="text-2xl font-semibold">Access Denied</h2>
        <p className="mt-2 text-sm text-gray-600">You do not have Quarantine Sites access.</p>
      </div>
    );
  }

  if (loading && !data) {
    return <OdrLoadingSpinner message="Loading Quarantine Sites" />;
  }

  if (error) {
    return (
      <div className="p-6">
        <h2 className="text-2xl font-semibold">Quarantine Sites</h2>
        <p className="mt-2 text-sm text-red-700">{error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <Card>
        <CardHeader className="gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <CardTitle>Quarantine Sites</CardTitle>
            <CardDescription>
              Quarantine registry over `zodr_quarantine_sites`. Inline site/date editing is supported directly in the table.
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={handleRefresh}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
            <Button onClick={openCreateDialog}>
              <Plus className="mr-2 h-4 w-4" />
              Add Row
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="quarantine-search">Search</Label>
              <Input
                id="quarantine-search"
                placeholder="Search quarantine rows"
                value={searchText}
                onChange={(event) => {
                  setSearchText(event.target.value);
                  setPage(1);
                }}
              />
            </div>
          </div>

          <div className="flex items-center justify-between text-sm text-gray-600">
            <div>
              Showing {items.length} of {totalCount} rows
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                Prev
              </Button>
              <span>
                Page {Math.min(page, totalPages)} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              >
                Next
              </Button>
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <button type="button" onClick={() => handleSort('id')}>ID</button>
                </TableHead>
                <TableHead>
                  <button type="button" onClick={() => handleSort('site')}>Housing Site</button>
                </TableHead>
                <TableHead>
                  <button type="button" onClick={() => handleSort('enter_date')}>Date Enter Quarantine</button>
                </TableHead>
                <TableHead>
                  <button type="button" onClick={() => handleSort('quarantined_through')}>Quarantined Through</button>
                </TableHead>
                <TableHead>Extended To 1</TableHead>
                <TableHead>Extended To 2</TableHead>
                <TableHead>Extended To 3</TableHead>
                <TableHead>Extended To 4</TableHead>
                <TableHead>Extended To 5</TableHead>
                <TableHead>
                  <button type="button" onClick={() => handleSort('updated')}>Updated</button>
                </TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.id}</TableCell>
                  {INLINE_FIELDS.map((field) => {
                    const isDateField = field !== 'site';
                    const cellKey = getCellKey(item.id, field);
                    return (
                      <TableCell key={field}>
                        <Input
                          type={isDateField ? 'date' : 'text'}
                          value={getCellValue(item, field)}
                          disabled={savingCellKey === cellKey}
                          onChange={(event) => setCellValue(item, field, event.target.value)}
                          onBlur={() => commitCellValue(item, field)}
                        />
                      </TableCell>
                    );
                  })}
                  <TableCell>{toTimestampLabel(item.updated)}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" onClick={() => openEditDialog(item)}>
                        <Pencil className="mr-2 h-3.5 w-3.5" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={isDeleting}
                        onClick={() => handleDelete(item)}
                      >
                        <Trash2 className="mr-2 h-3.5 w-3.5" />
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent contentWidth="760px" contentHeight="80vh" contentMaxWidth="95vw">
          <DialogHeader>
            <DialogTitle>{formState.id === null ? 'Add Quarantine Site' : 'Edit Quarantine Site'}</DialogTitle>
            <DialogDescription>
              Full-row edit form over `zodr_quarantine_sites`. No legacy status toggle is carried into this replacement.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 overflow-y-auto pr-2 md:grid-cols-2">
            {formFields.map((field) => {
              if (field.key === 'id') {
                return null;
              }

              return (
                <div key={field.key} className="space-y-2">
                  <Label>{field.label}</Label>
                  <Input
                    type={field.type === 'date' ? 'date' : 'text'}
                    value={formState[field.key]}
                    onChange={(event) => setField(field.key, event.target.value)}
                  />
                </div>
              );
            })}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" disabled={isSaving} onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button disabled={isSaving} onClick={handleSave}>
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
